import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDatabase, ref, set, update } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { saveNewTemplate } from '../services/templateService';
import { maintenanceTemplateApi } from '../../../../api/maintenanceTemplateApi';
import { ComponentWithReplacement, GroupedComponents, MaintenanceDraftWithDone } from '../types';
import { ComponentLifetime, TemplatesByType, EditForm, MaintenanceTemplate, CostUnit } from '../../TemplatesEditor/types';
import { Building, ValueStatus } from '../../../../types/protocols';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { recordActivity } from '../../../../api/activityApi';
import { ComponentMaintenanceData, MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';
import { CostSettings } from '../utils/costSettings';
import * as validation from '../utils/validation';

/**
 * Cost settings modal context - tracks which activity is being edited
 */
export interface CostSettingsContext {
    /** The component attachments are uploaded under */
    componentKey: string;
    /** Every component the settings are written to (Massredigering: the selected ones with the row) */
    componentKeys: string[];
    templateId: string;
    templateName: string;
    activity: MaintenanceActivity;
    /** Schablon cost: template unit price × quantity */
    calculatedCost: number;
    /** The quantity a unit price is multiplied by (m² / lm, custom quantity included); null for kr/st */
    quantity: number | null;
    costExplanation: string;
    componentType: string;
    componentTypeName: string;
    templateCost: number;
    templateCostUnit: CostUnit;
    templatePrimaryComponentType: string;
}

/**
 * Replacement cost modal context - tracks which component is being edited
 */
export interface ReplacementCostContext {
    /** The component attachments are uploaded under */
    componentKey: string;
    /** Every component the settings are written to (Massredigering: every selected one) */
    componentKeys: string[];
    componentName: string;
    /** What the modal starts from: the component's draft entry, or an empty one when the selection disagrees */
    componentData: ComponentMaintenanceData;
    /** Schablon cost: lifetime unit price × quantity */
    calculatedCost: number;
    /** The quantity a unit price is multiplied by (m² / lm); null for kr/st */
    quantity: number | null;
    costExplanation: string;
    componentType: string;
    componentTypeName: string;
    templateCost: number;
    templateCostUnit: CostUnit;
}

interface UseModalManagementProps {
    buildingKey: string | undefined;
    building: Building | null;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    maintenanceDraft: MaintenanceDraftWithDone;
    groupedComponents: GroupedComponents;
    loading: boolean;
    setBuilding: (building: Building) => void;
    /** The components whose draft dates are recalculated once the building state carries their new condition */
    pendingRecalculationRef: React.MutableRefObject<string[] | null>;
    setMaintenanceTemplates: React.Dispatch<React.SetStateAction<TemplatesByType>>;
    refreshComponentTypes: () => Promise<void>;
    // Cost settings handlers from useMaintenanceHandlers
    handleActivityCostSettingsSave: (componentKeys: string[], templateId: string, settings: CostSettings) => void;
    handleReplacementCostSettingsSave: (componentKeys: string[], settings: CostSettings) => void;
}

interface UseModalManagementReturn {
    // Delete modal
    showDeleteModal: boolean;
    deleting: boolean;
    handleDeleteDraft: () => void;
    handleConfirmDeleteDraft: () => Promise<void>;
    handleCancelDeleteDraft: () => void;

    // Preview modal
    showPreviewModal: boolean;
    handlePreview: () => void;
    handleClosePreview: () => void;

    // Condition modal: one component, or every selected one in Massredigering
    showConditionModal: boolean;
    componentsForCondition: ComponentWithReplacement[];
    handleOpenConditionModal: (plans: ComponentWithReplacement[]) => void;
    handleCloseConditionModal: () => void;
    handleSaveCondition: (conditionValue: number, comment: string) => Promise<void>;

    // Lifetime modal
    showLifetimeModal: boolean;
    componentTypeForLifetime: string | null;
    savingLifetime: boolean;
    handleOpenLifetimeModal: (componentType: string) => void;
    handleCloseLifetimeModal: () => void;
    handleSaveLifetime: (lifetime: ComponentLifetime, setComponentLifetimes: (updater: (prev: Record<string, ComponentLifetime>) => Record<string, ComponentLifetime>) => void) => Promise<void>;

    // New Activity modal
    showNewActivityModal: boolean;
    componentTypeForNewActivity: string | null;
    savingNewActivity: boolean;
    handleOpenNewActivityModal: (componentType: string) => void;
    handleCloseNewActivityModal: () => void;
    /** Resolves to the created template, or null when nothing was saved */
    handleSaveNewActivity: (templateForm: EditForm) => Promise<MaintenanceTemplate | null>;

    // Cost settings modal
    showCostSettingsModal: boolean;
    costSettingsContext: CostSettingsContext | null;
    handleOpenCostSettingsModal: (context: CostSettingsContext) => void;
    handleCloseCostSettingsModal: () => void;
    handleSaveCostSettings: (settings: CostSettings) => void;

    // Replacement cost modal
    showReplacementCostModal: boolean;
    replacementCostContext: ReplacementCostContext | null;
    handleOpenReplacementCostModal: (context: ReplacementCostContext) => void;
    handleCloseReplacementCostModal: () => void;
    handleSaveReplacementCost: (settings: CostSettings) => void;

    // Bulk apply modal
    showBulkApplyModal: boolean;
    bulkApplySourceKey: string | null;
    handleOpenBulkApplyModal: (sourceKey: string) => void;
    handleCloseBulkApplyModal: () => void;
}

export const useModalManagement = ({
    buildingKey,
    building,
    componentLifetimes,
    maintenanceTemplates,
    maintenanceDraft,
    groupedComponents,
    loading,
    setBuilding,
    pendingRecalculationRef,
    setMaintenanceTemplates,
    refreshComponentTypes,
    handleActivityCostSettingsSave,
    handleReplacementCostSettingsSave
}: UseModalManagementProps): UseModalManagementReturn => {
    const navigate = useNavigate();
    const db = getDatabase();

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    // Preview modal state
    const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);

    // Condition modal state
    const [showConditionModal, setShowConditionModal] = useState<boolean>(false);
    const [componentsForCondition, setComponentsForCondition] = useState<ComponentWithReplacement[]>([]);

    // Lifetime modal state
    const [showLifetimeModal, setShowLifetimeModal] = useState<boolean>(false);
    const [componentTypeForLifetime, setComponentTypeForLifetime] = useState<string | null>(null);
    const [savingLifetime, setSavingLifetime] = useState<boolean>(false);

    // New Activity modal state
    const [showNewActivityModal, setShowNewActivityModal] = useState<boolean>(false);
    const [componentTypeForNewActivity, setComponentTypeForNewActivity] = useState<string | null>(null);
    const [savingNewActivity, setSavingNewActivity] = useState<boolean>(false);

    // Cost Settings modal state
    const [showCostSettingsModal, setShowCostSettingsModal] = useState<boolean>(false);
    const [costSettingsContext, setCostSettingsContext] = useState<CostSettingsContext | null>(null);

    // Replacement Cost modal state
    const [showReplacementCostModal, setShowReplacementCostModal] = useState<boolean>(false);
    const [replacementCostContext, setReplacementCostContext] = useState<ReplacementCostContext | null>(null);

    // Bulk Apply modal state
    const [showBulkApplyModal, setShowBulkApplyModal] = useState<boolean>(false);
    const [bulkApplySourceKey, setBulkApplySourceKey] = useState<string | null>(null);

    // Delete draft handlers
    const handleDeleteDraft = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const handleConfirmDeleteDraft = useCallback(async (): Promise<void> => {
        if (!buildingKey) return;

        setDeleting(true);
        try {
            await set(ref(db, `maintenance/drafts/${buildingKey}`), null);
            recordActivity('maintenancePlan.deleted', { buildingKey, targetKey: buildingKey });
            navigate(`/maintenanceActive/${buildingKey}`);
        } catch (error) {
            console.error('Error deleting draft:', error);
            alert('Ett fel uppstod vid radering av utkastet. Försök igen.');
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    }, [buildingKey, db, navigate]);

    const handleCancelDeleteDraft = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    // Preview modal handlers
    const handlePreview = useCallback(() => {
        setShowPreviewModal(true);
    }, []);

    const handleClosePreview = useCallback(() => {
        setShowPreviewModal(false);
    }, []);

    // Condition modal handlers
    const handleOpenConditionModal = useCallback((plans: ComponentWithReplacement[]) => {
        if (plans.length === 0) return;
        setComponentsForCondition(plans);
        setShowConditionModal(true);
    }, []);

    const handleCloseConditionModal = useCallback(() => {
        setShowConditionModal(false);
        setComponentsForCondition([]);
    }, []);

    // The same assessment on every component, in one root update
    const handleSaveCondition = useCallback(async (conditionValue: number, comment: string): Promise<void> => {
        if (componentsForCondition.length === 0 || !buildingKey) return;

        const componentKeys = componentsForCondition.map(plan => plan.component.key);

        try {
            const auth = getAuth();
            const conditionData = {
                value: {
                    value: conditionValue,
                    ...(comment?.trim() && { comment: comment.trim() })
                },
                updatedWhen: formatDateForStorage(new Date()),
                updatedBy: auth.currentUser?.uid || '',
                status: ValueStatus.set,
                assessedAt: formatDateForStorage(new Date())
            };

            const updates: Record<string, unknown> = {};
            for (const componentKey of componentKeys) {
                updates[`buildings/${buildingKey}/structure/components/${componentKey}/condition`] = conditionData;
            }
            await update(ref(db), updates);
            recordActivity('component.updated', {
                buildingKey,
                ...(componentKeys.length === 1 && { targetKey: componentKeys[0] })
            });

            pendingRecalculationRef.current = componentKeys;

            if (building?.model?.structure?.components) {
                const components = { ...building.model.structure.components };
                for (const componentKey of componentKeys) {
                    if (components[componentKey]) {
                        components[componentKey] = { ...components[componentKey], condition: conditionData };
                    }
                }
                setBuilding({
                    ...building,
                    model: {
                        ...building.model,
                        structure: { ...building.model.structure, components }
                    }
                });
            }

            handleCloseConditionModal();
        } catch (error) {
            console.error('Error saving condition:', error);
            throw error;
        }
    }, [componentsForCondition, buildingKey, db, pendingRecalculationRef, building, setBuilding, handleCloseConditionModal]);

    // Lifetime modal handlers
    const handleOpenLifetimeModal = useCallback((componentType: string): void => {
        setComponentTypeForLifetime(componentType);
        setShowLifetimeModal(true);
    }, []);

    const handleCloseLifetimeModal = useCallback((): void => {
        setShowLifetimeModal(false);
        setComponentTypeForLifetime(null);
    }, []);

    const handleSaveLifetime = useCallback(async (
        lifetime: ComponentLifetime,
        setComponentLifetimes: (updater: (prev: Record<string, ComponentLifetime>) => Record<string, ComponentLifetime>) => void
    ): Promise<void> => {
        if (!componentTypeForLifetime) return;

        try {
            setSavingLifetime(true);

            const saved = await maintenanceTemplateApi.saveLifetime(
                componentTypeForLifetime, lifetime, { source: 'draft', buildingKey }
            );

            setComponentLifetimes(prev => ({ ...prev, [componentTypeForLifetime]: saved }));

            setShowLifetimeModal(false);
            setComponentTypeForLifetime(null);
        } catch (error) {
            console.error('Error saving lifetime:', error);
            alert(error instanceof Error ? error.message : 'Fel vid sparande av livslängd');
        } finally {
            setSavingLifetime(false);
        }
    }, [componentTypeForLifetime]);

    // New Activity handlers
    const handleOpenNewActivityModal = useCallback((componentType: string): void => {
        setComponentTypeForNewActivity(componentType);
        setShowNewActivityModal(true);
    }, []);

    const handleCloseNewActivityModal = useCallback((): void => {
        setShowNewActivityModal(false);
        setComponentTypeForNewActivity(null);
    }, []);

    const handleSaveNewActivity = useCallback(async (templateForm: EditForm): Promise<MaintenanceTemplate | null> => {
        if (!componentTypeForNewActivity) return null;

        try {
            setSavingNewActivity(true);

            // Ensure primaryComponentType is set on the form
            const formWithPrimaryType: EditForm = {
                ...templateForm,
                primaryComponentType: templateForm.primaryComponentType || componentTypeForNewActivity
            };

            const templateId = await saveNewTemplate(formWithPrimaryType);

            // Refresh context to reload templates from Firebase
            await refreshComponentTypes();

            handleCloseNewActivityModal();

            const { templateKey: _templateKey, ...template } = formWithPrimaryType;
            return { ...template, id: templateId };
        } catch (error) {
            console.error('Error saving new activity:', error);
            alert('Fel vid sparande av ny aktivitet');
            return null;
        } finally {
            setSavingNewActivity(false);
        }
    }, [componentTypeForNewActivity, refreshComponentTypes, handleCloseNewActivityModal]);

    // Cost Settings modal handlers
    const handleOpenCostSettingsModal = useCallback((context: CostSettingsContext): void => {
        setCostSettingsContext(context);
        setShowCostSettingsModal(true);
    }, []);

    const handleCloseCostSettingsModal = useCallback((): void => {
        setShowCostSettingsModal(false);
        setCostSettingsContext(null);
    }, []);

    // Massredigering: the same settings (attachments included, one upload) on every component with the row
    const handleSaveCostSettings = useCallback((settings: CostSettings): void => {
        if (!costSettingsContext) return;
        handleActivityCostSettingsSave(costSettingsContext.componentKeys, costSettingsContext.templateId, settings);
        handleCloseCostSettingsModal();
    }, [costSettingsContext, handleActivityCostSettingsSave, handleCloseCostSettingsModal]);

    // Replacement Cost modal handlers
    const handleOpenReplacementCostModal = useCallback((context: ReplacementCostContext): void => {
        setReplacementCostContext(context);
        setShowReplacementCostModal(true);
    }, []);

    const handleCloseReplacementCostModal = useCallback((): void => {
        setShowReplacementCostModal(false);
        setReplacementCostContext(null);
    }, []);

    const handleSaveReplacementCost = useCallback((settings: CostSettings): void => {
        if (!replacementCostContext) return;
        handleReplacementCostSettingsSave(replacementCostContext.componentKeys, settings);
        handleCloseReplacementCostModal();
    }, [replacementCostContext, handleReplacementCostSettingsSave, handleCloseReplacementCostModal]);

    // Bulk Apply modal handlers
    const handleOpenBulkApplyModal = useCallback((sourceKey: string): void => {
        setBulkApplySourceKey(sourceKey);
        setShowBulkApplyModal(true);
    }, []);

    const handleCloseBulkApplyModal = useCallback((): void => {
        setShowBulkApplyModal(false);
        setBulkApplySourceKey(null);
    }, []);

    return {
        showDeleteModal,
        deleting,
        handleDeleteDraft,
        handleConfirmDeleteDraft,
        handleCancelDeleteDraft,

        showPreviewModal,
        handlePreview,
        handleClosePreview,

        showConditionModal,
        componentsForCondition,
        handleOpenConditionModal,
        handleCloseConditionModal,
        handleSaveCondition,

        showLifetimeModal,
        componentTypeForLifetime,
        savingLifetime,
        handleOpenLifetimeModal,
        handleCloseLifetimeModal,
        handleSaveLifetime,

        showNewActivityModal,
        componentTypeForNewActivity,
        savingNewActivity,
        handleOpenNewActivityModal,
        handleCloseNewActivityModal,
        handleSaveNewActivity,

        showCostSettingsModal,
        costSettingsContext,
        handleOpenCostSettingsModal,
        handleCloseCostSettingsModal,
        handleSaveCostSettings,

        showReplacementCostModal,
        replacementCostContext,
        handleOpenReplacementCostModal,
        handleCloseReplacementCostModal,
        handleSaveReplacementCost,

        showBulkApplyModal,
        bulkApplySourceKey,
        handleOpenBulkApplyModal,
        handleCloseBulkApplyModal
    };
};
